import { ReactElement, useCallback } from 'react'
import { useFieldArray } from 'react-hook-form'
import { findIndex, find, cloneDeep } from 'lodash-es'
import { v4 as uuid } from 'uuid'
import { useSnackbar } from '@ui/snackbar'
import { useElementLoadStore, useModelStore } from '@editorStores'
import { useElementType } from '@editorHooks'
import { useStructuralPlanningSelection } from '@structuralPlanningHooks'
import { createAreaLoadSchema } from '../schema'
import { getElementAreaOfAttack } from '../utils'

interface Methods {
  onAdd: () => void
  onRemove: (guid: string) => void
  onCopy: (guid: string) => void
  loads: ElementLoad[]
}

interface Props {
  children: ({ onAdd, onRemove, onCopy, loads }: Methods) => ReactElement
}

const AreaLoadFieldArray = ({ children }: Props) => {
  const { enqueueSnackbar } = useSnackbar()

  const {
    params: { loadType, selectedElements, load: activeLoadGuid },
    actions: { selectLoad: setLoad, clearLoadSelection },
  } = useStructuralPlanningSelection()
  const { remove, fields } = useFieldArray({
    name: loadType as string,
  })
  const addLoad = useElementLoadStore(state => state.addLoadPerElement)
  const removeLoad = useElementLoadStore(state => state.removeLoad)
  const loads = fields as unknown as ElementLoad[]
  const selectedElementType = useElementType(selectedElements?.[0])
  const verticalSlabs = useModelStore(state => state.model.vertical_slabs)
  const verticalRoofSlabs = useModelStore(state => state.model.vertical_roof_slabs)

  const onAdd = useCallback(() => {
    const selectedElement = selectedElements?.[0]

    if (!selectedElement) return

    if (selectedElementType !== 'vertical_slabs' && selectedElementType !== 'vertical_roof_slabs') {
      enqueueSnackbar(
        'Flächenlasten können nur auf Deckenpositionen und Dachpositionen aufgeprägt werden',
        { variant: 'info' },
      )
      return
    }

    const areaOfAttack = getElementAreaOfAttack(
      [...verticalSlabs, ...verticalRoofSlabs],
      selectedElement,
    )

    if (!areaOfAttack) return

    // new area loads cover the entire element, a custom area can be drawn afterwards
    const newLoad = {
      ...createAreaLoadSchema().getDefault(),
      element_guid: selectedElement,
      area_of_attack: areaOfAttack,
      domain_guid: uuid(),
      guid: uuid(),
    } as AreaLoad

    addLoad(newLoad)
    setLoad(newLoad)
  }, [
    addLoad,
    enqueueSnackbar,
    selectedElementType,
    selectedElements,
    setLoad,
    verticalRoofSlabs,
    verticalSlabs,
  ])

  const onRemove = (guid: string) => {
    const index = findIndex(fields, ['guid', guid])
    remove(index)
    removeLoad(guid)
    if (guid === activeLoadGuid) clearLoadSelection()
  }

  const onCopy = (guid: string) => {
    const load = find(loads, { guid }) as ElementLoad
    const newLoad = {
      ...cloneDeep(load),
      guid: uuid(),
    }
    addLoad(newLoad)
    setLoad(newLoad as ElementLoad)
  }

  return <>{children({ onAdd, onRemove, onCopy, loads })}</>
}

export default AreaLoadFieldArray
